import { useCallback, useEffect, useRef, useState } from 'react'
import useBracketStore from '@/stores/useBracketStore'

export default function useBracketScroll({
  isMobile,
  bracketRoundCount,
  bracketStructure,
  matches,
  viewMode,
}) {
  const bracketScrollRef = useRef(null)
  const bracketTopScrollRef = useRef(null)
  const bracketInnerRef = useRef(null)
  const dragStateRef = useRef({ isDown: false, startX: 0, startScrollLeft: 0, pointerId: null })
  const stickyHeaderRef = useRef(null)
  const matchRefs = useRef(new Map())

  const [isHeaderSticky, setIsHeaderSticky] = useState(false)
  const [bracketHeight, setBracketHeight] = useState(null)
  const [bracketScrollWidth, setBracketScrollWidth] = useState(0)

  // Read shared state from store
  const mobileRoundIndex = useBracketStore(s => s.mobileRoundIndex)
  const setMobileRoundIndex = useBracketStore(s => s.setMobileRoundIndex)
  const displayRoundIndex = useBracketStore(s => s.displayRoundIndex)
  const setDisplayRoundIndex = useBracketStore(s => s.setDisplayRoundIndex)
  const setShowBackToTop = useBracketStore(s => s.setShowBackToTop)
  const loading = useBracketStore(s => s.loading)
  const loadingPicks = useBracketStore(s => s.loadingPicks)

  const measureBracketWidth = useCallback(() => {
    const el = bracketInnerRef.current
    if (!el) return
    setBracketScrollWidth(el.scrollWidth)
  }, [])

  // Measure bracket width
  useEffect(() => {
    let raf = requestAnimationFrame(measureBracketWidth)
    const onResize = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(measureBracketWidth)
    }
    window.addEventListener('resize', onResize)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', onResize)
    }
  }, [measureBracketWidth, bracketRoundCount, matches.length, viewMode, isMobile])

  // Pin horizontal scrollbar to viewport bottom
  useEffect(() => {
    if (isMobile) {
      setBracketHeight(null)
      return
    }
    const el = bracketScrollRef.current
    if (!el) return
    const updateHeight = () => {
      const rect = el.getBoundingClientRect()
      const h = window.innerHeight - rect.top - 8
      if (h > 0) setBracketHeight(h)
    }
    const raf = requestAnimationFrame(updateHeight)
    window.addEventListener('resize', updateHeight)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', updateHeight)
    }
  }, [isMobile, viewMode, matches.length])

  // Sync top scrollbar with main scrollbar
  useEffect(() => {
    if (isMobile) return
    const topEl = bracketTopScrollRef.current
    const mainEl = bracketScrollRef.current
    if (!topEl || !mainEl) return

    let syncing = false
    const syncFromMain = () => {
      if (syncing) return
      syncing = true
      topEl.scrollLeft = mainEl.scrollLeft
      syncing = false
    }
    const syncFromTop = () => {
      if (syncing) return
      syncing = true
      mainEl.scrollLeft = topEl.scrollLeft
      syncing = false
    }

    mainEl.addEventListener('scroll', syncFromMain, { passive: true })
    topEl.addEventListener('scroll', syncFromTop, { passive: true })
    syncFromMain()
    return () => {
      mainEl.removeEventListener('scroll', syncFromMain)
      topEl.removeEventListener('scroll', syncFromTop)
    }
  }, [isMobile, bracketScrollWidth])

  // Detect when sticky header becomes stuck
  useEffect(() => {
    const headerEl = stickyHeaderRef.current
    if (!headerEl) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsHeaderSticky(!entry.isIntersecting)
      },
      { threshold: [1], rootMargin: '-1px 0px 0px 0px' }
    )

    observer.observe(headerEl)
    return () => observer.disconnect()
  }, [])

  // Show/hide back-to-top button (writes to store)
  useEffect(() => {
    const el = bracketScrollRef.current
    const handleScroll = () => {
      const windowScrolled = window.scrollY > 300
      const bracketEl = bracketScrollRef.current
      const bracketScrolled = bracketEl ? bracketEl.scrollTop > 300 : false
      setShowBackToTop(windowScrolled || bracketScrolled)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    if (el) el.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => {
      window.removeEventListener('scroll', handleScroll)
      if (el) el.removeEventListener('scroll', handleScroll)
    }
  }, [matches.length, viewMode, setShowBackToTop])

  // Reset mobile round index on bracket change
  useEffect(() => {
    if (!bracketRoundCount) {
      setMobileRoundIndex(0)
      return
    }
    if (bracketRoundCount > 0 && mobileRoundIndex > bracketRoundCount - 1) {
      setMobileRoundIndex(bracketRoundCount - 1)
    }
  }, [bracketRoundCount, mobileRoundIndex, setMobileRoundIndex])

  // DUB-102: once the bracket and picks are both loaded, jump to the round
  // the user was last working on so returning users don't have to
  // swipe/scroll past completed rounds. Mobile: sets the carousel round.
  // Desktop: scrolls the bracket container horizontally so the target
  // round's first match is in view. Guarded with a ref so it runs once
  // per page mount.
  //
  // Race-condition note: loadEntryPicks is called from BracketPicks.jsx
  // *after* matches load (via a parent useEffect). Child effects run before
  // parent effects on the same commit, so the very first time this effect
  // runs it sees loadingPicks=false (its default) — not "picks finished
  // loading," but "loadEntryPicks hasn't been called yet." We have to wait
  // for loadingPicks to transition true → false to know picks are settled.
  const didInitDefaultRoundRef = useRef(false)
  const sawLoadingPicksRef = useRef(false)
  useEffect(() => {
    if (loadingPicks) sawLoadingPicksRef.current = true
  }, [loadingPicks])
  useEffect(() => {
    if (didInitDefaultRoundRef.current) return
    if (!bracketStructure?.length) return
    if (loading || loadingPicks) return
    if (!sawLoadingPicksRef.current) return
    didInitDefaultRoundRef.current = true
    const idx = useBracketStore.getState().computeInitialRoundIndex()
    if (idx <= 0) return
    if (isMobile) {
      setMobileRoundIndex(idx)
      return
    }
    // Desktop: scroll the bracket container so the first match of the target
    // round centers horizontally. Wait two frames for layout (sticky labels,
    // round columns) to settle before measuring.
    const targetMatch = bracketStructure[idx]?.matches?.[0]
    if (!targetMatch) return
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const matchEl = matchRefs.current.get(targetMatch.match_id)
        const container = bracketScrollRef.current
        if (!matchEl || !container) return
        const matchRect = matchEl.getBoundingClientRect()
        const containerRect = container.getBoundingClientRect()
        const relativeLeft = matchRect.left - containerRect.left
        const targetScrollLeft = container.scrollLeft + relativeLeft - (containerRect.width / 2) + (matchRect.width / 2)
        container.scrollTo({ left: Math.max(0, targetScrollLeft), behavior: 'auto' })
      })
    })
  }, [bracketStructure, loading, loadingPicks, isMobile, setMobileRoundIndex])

  // Sync display round index with mobile round index
  useEffect(() => {
    setDisplayRoundIndex(mobileRoundIndex)
  }, [mobileRoundIndex, setDisplayRoundIndex])

  const handleBackToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    const el = bracketScrollRef.current
    if (el) el.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const handleBracketPointerDown = useCallback((e) => {
    if (isMobile) return
    if (e.pointerType === 'mouse' && e.button !== 0) return
    if (e.target?.closest?.('button,a,input,textarea,select,[role="button"],.pointer-events-auto')) return

    const el = bracketScrollRef.current
    if (!el) return

    dragStateRef.current.isDown = true
    dragStateRef.current.startX = e.clientX
    dragStateRef.current.startScrollLeft = el.scrollLeft
    dragStateRef.current.pointerId = e.pointerId

    if (el.setPointerCapture) el.setPointerCapture(e.pointerId)
  }, [isMobile])

  const handleBracketPointerMove = useCallback((e) => {
    const el = bracketScrollRef.current
    if (!el) return
    if (!dragStateRef.current.isDown) return

    const dx = e.clientX - dragStateRef.current.startX
    el.scrollLeft = dragStateRef.current.startScrollLeft - dx
  }, [])

  const handleBracketPointerUp = useCallback(() => {
    dragStateRef.current.isDown = false
    dragStateRef.current.pointerId = null
  }, [])

  // Scroll to a match with smooth animation.
  // Desktop: skips vertical scrolling when the match card already sits
  // comfortably inside the viewport — that avoids the "jumps up then down"
  // glitch users saw after making a pick that didn't require moving to a
  // different round (DUB-36, DUB-43).
  // Mobile: always pans the page so the next match centers vertically, giving
  // each pick clear forward-motion feedback.
  //
  // Timing: this function is called from handleSelectWinner immediately after
  // a pick. Different platforms need different scheduling:
  //  - Desktop: double-rAF flushes React's commit so getBoundingClientRect
  //    measures the post-pick layout, not the pre-pick one (DUB-106..109).
  //  - Mobile: a small setTimeout lets the post-pick rerender + tap-feedback
  //    transitions paint BEFORE the smooth-scroll starts. iOS Safari drops
  //    frames if the main thread is still doing layout/paint work mid-scroll,
  //    which produces visible choppiness during the animation. The original
  //    setTimeout(300) buried this; double-rAF (which is what desktop uses)
  //    fires too early on mobile.
  const MOBILE_SETTLE_MS = 150
  const scrollToMatch = useCallback((match) => {
    if (!match) return

    // Mobile: always pan the page so the next match centers vertically — gives
    // each pick a clear "moved on to the next one" feedback, even when the
    // next match is already partially visible. Horizontal positioning is
    // handled by the carousel (see RoundCarousel translateX) and must NOT be
    // touched here — DUB-103 / DUB-104 / DUB-105 came from calling
    // scrollIntoView({ inline: 'center' }) mid-carousel-animation, which
    // shoved the page-level horizontal scroll and left cards flush right.
    if (isMobile) {
      const scrollPageToMatch = () => {
        const matchElement = matchRefs.current.get(match.match_id)
        if (!matchElement) return
        const rect = matchElement.getBoundingClientRect()
        const vh = window.innerHeight
        const targetY = window.scrollY + rect.top - (vh / 2) + (rect.height / 2)
        window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' })
      }

      if (bracketStructure.length > 0) {
        const targetRoundIndex = bracketStructure.findIndex(r => r.roundNum === match.round)
        if (targetRoundIndex !== -1 && targetRoundIndex !== mobileRoundIndex) {
          setMobileRoundIndex(targetRoundIndex)
          // Cross-round: settle delay + two rAFs (rAFs let the new round
          // panel mount before measuring; the settle lets render finish
          // before the scroll competes for frames).
          setTimeout(() => {
            requestAnimationFrame(() => requestAnimationFrame(scrollPageToMatch))
          }, MOBILE_SETTLE_MS)
          return
        }
      }
      // Same-round: just settle, then scroll.
      setTimeout(scrollPageToMatch, MOBILE_SETTLE_MS)
      return
    }

    // Desktop: double-rAF guarantees we measure positions on the frame after
    // React commits the post-pick layout (DUB-106..109).
    const runDesktopScroll = () => {
      const matchElement = matchRefs.current.get(match.match_id)
      if (!matchElement) return

      const scrollContainer = bracketScrollRef.current
      if (!scrollContainer) return

      const matchRect = matchElement.getBoundingClientRect()
      const containerRect = scrollContainer.getBoundingClientRect()

      const relativeLeft = matchRect.left - containerRect.left
      const containerWidth = containerRect.width
      const matchWidth = matchRect.width
      const targetScrollLeft = scrollContainer.scrollLeft + relativeLeft - (containerWidth / 2) + (matchWidth / 2)

      const relativeTop = matchRect.top - containerRect.top
      const containerHeight = containerRect.height
      const matchHeight = matchRect.height

      // Consider the match "already visible" if its top and bottom both sit
      // inside the relevant viewport with a small margin. Skip the vertical
      // scroll in that case — only sync the horizontal position.
      const MARGIN = 24
      const containerOverflowsVertically = scrollContainer.scrollHeight > scrollContainer.clientHeight

      if (containerOverflowsVertically) {
        const topVisible = relativeTop >= MARGIN
        const bottomVisible = relativeTop + matchHeight <= containerHeight - MARGIN
        if (topVisible && bottomVisible) {
          scrollContainer.scrollTo({ left: Math.max(0, targetScrollLeft), behavior: 'smooth' })
          return
        }
        const targetScrollTop = scrollContainer.scrollTop + relativeTop - (containerHeight / 2) + (matchHeight / 2)
        scrollContainer.scrollTo({
          top: Math.max(0, targetScrollTop),
          left: Math.max(0, targetScrollLeft),
          behavior: 'smooth',
        })
      } else {
        const viewportHeight = window.innerHeight
        const topVisible = matchRect.top >= MARGIN
        const bottomVisible = matchRect.bottom <= viewportHeight - MARGIN
        if (topVisible && bottomVisible) {
          scrollContainer.scrollTo({ left: Math.max(0, targetScrollLeft), behavior: 'smooth' })
          return
        }
        const targetPageScrollY = window.scrollY + matchRect.top - (viewportHeight / 2) + (matchHeight / 2)
        window.scrollTo({
          top: Math.max(0, targetPageScrollY),
          behavior: 'smooth',
        })
        scrollContainer.scrollTo({
          left: Math.max(0, targetScrollLeft),
          behavior: 'smooth',
        })
      }
    }

    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => requestAnimationFrame(runDesktopScroll))
    } else {
      runDesktopScroll()
    }
  }, [isMobile, mobileRoundIndex, bracketStructure, setMobileRoundIndex])

  const goToRound = useCallback((index) => {
    const clamped = Math.max(0, Math.min(bracketStructure.length - 1, index))
    const current = useBracketStore.getState().mobileRoundIndex
    if (clamped === current) return
    setMobileRoundIndex(clamped)
  }, [bracketStructure.length, setMobileRoundIndex])

  return {
    // Refs
    bracketScrollRef,
    bracketTopScrollRef,
    bracketInnerRef,
    stickyHeaderRef,
    matchRefs,

    // State
    isHeaderSticky,
    bracketHeight,
    bracketScrollWidth,
    mobileRoundIndex,
    setMobileRoundIndex,
    displayRoundIndex,
    setDisplayRoundIndex,

    // Handlers
    handleBackToTop,
    handleBracketPointerDown,
    handleBracketPointerMove,
    handleBracketPointerUp,
    scrollToMatch,
    goToRound,
  }
}
